import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from "../../provider/authProvider";
import { useSignalRContext } from '../../SignalR/useSignalR';
import { toast } from 'react-toastify';
import {
  Box,
  Modal,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  InputAdornment,
  IconButton,
  FormHelperText,
  Autocomplete
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const ManualOrderClose = ({ open, onClose }) => {
  const { token } = useAuth();
  const { sendClosePositionRequest, symbolsList } = useSignalRContext();
  const [loading, setLoading] = useState(false);
  const [symbols, setSymbols] = useState([]);
  const [formData, setFormData] = useState({
    symbol: null,
    quantity: '',
    clOrdId: `close_${Date.now()}`,
    side: '2', // Default to Sell for closing positions
    designation: ''
  });

  // Update symbols list when symbolsList changes
  useEffect(() => {
    if (symbolsList && symbolsList.length > 0) {
      setSymbols(symbolsList.map(symbol => ({
        id: symbol.id,
        name: symbol.name,
        label: `${symbol.name} (${symbol.id})`
      })));
    }
  }, [symbolsList]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSymbolChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      symbol: newValue
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.symbol) {
      toast.error('Please select a symbol');
      return;
    }
    
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    setLoading(true);
    try {
      // Create a unique ClOrdID if not provided
      const clOrdId = formData.clOrdId || `close_${Date.now()}`;
      
      // Call the backend to close the position
      await sendClosePositionRequest(
        token, 
        {
          SymbolId: formData.symbol.id,
          SymbolName: formData.symbol.name,
          Quantity: parseFloat(formData.quantity),
          ClOrdId: clOrdId,
          TradeSide: formData.side, // '1' for Buy, '2' for Sell
          Designation: formData.designation || undefined
        }
      );
      
      toast.success('Position close request sent successfully');
      onClose();
    } catch (error) {
      console.error('Error closing position:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to close position';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="manual-close-modal"
      aria-describedby="manual-close-order"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 500,
        maxHeight: '90vh',
        overflowY: 'auto',
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 1,
        outline: 'none'
      }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2">
              Close Position
            </Typography>
            <IconButton 
              onClick={onClose}
              disabled={loading}
              color="inherit"
              size="small"
              sx={{ p: 0.5 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          
          <Box component="form" onSubmit={handleSubmit} sx={{ '& > :not(style)': { mb: 2 } }}>
            {/* Symbol Selection */}
            <FormControl fullWidth margin="normal">
              <Autocomplete
                id="symbol-select"
                options={symbols}
                getOptionLabel={(option) => option.label || ''}
                value={formData.symbol}
                onChange={handleSymbolChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Symbol"
                    required
                    placeholder="Search symbol..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <React.Fragment>
                          {loading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
                disabled={loading}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </FormControl>

            {/* Quantity */}
            <TextField
              fullWidth
              label="Quantity"
              name="quantity"
              type="number"
              value={formData.quantity}
              onChange={handleChange}
              required
              disabled={loading}
              margin="normal"
              inputProps={{
                step: '0.01',
                min: '0.01'
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={() => {
                        // Auto-fill with position size if available
                        // You can implement this based on your position data
                      }}
                      disabled={loading}
                    >
                      Max
                    </Button>
                  </InputAdornment>
                ),
              }}
            />

            {/* ClOrdID */}
            <TextField
              fullWidth
              label="Client Order ID"
              name="clOrdId"
              value={formData.clOrdId}
              onChange={handleChange}
              disabled={loading}
              margin="normal"
              helperText="Leave empty to auto-generate"
            />

            {/* Side */}
            <FormControl fullWidth margin="normal">
              <InputLabel>Side</InputLabel>
              <Select
                name="side"
                value={formData.side}
                onChange={handleChange}
                label="Side"
                disabled={loading}
                required
              >
                <MenuItem value="1">Buy</MenuItem>
                <MenuItem value="2">Sell</MenuItem>
              </Select>
              <FormHelperText>Opposite of the position side to close</FormHelperText>
            </FormControl>

            {/* Designation */}
            <TextField
              fullWidth
              label="Designation (Optional)"
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              disabled={loading}
              margin="normal"
              helperText="Optional order designation"
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="error"
                disabled={loading || !formData.symbol || !formData.quantity}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Processing...' : 'Close Position'}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Modal>
  );
};

export default ManualOrderClose;
